/**
 * Where a food's numbers come from, in plain words, and how complete they are. Replaces the bare
 * "72/94" nutrient count: the count is still there, in the detail line and the tooltip.
 */
import { TOTAL_NUTRIENTS } from './foodNutrients';
import type { FoodSource } from './types';

export type TrustTone = 'lab' | 'label' | 'mine' | 'ai' | 'recipe';

export interface Trust {
  /** Short badge text. */
  label: string;
  tone: TrustTone;
  /** What that means, one sentence. */
  meaning: string;
  /** "Full nutrient detail" etc. */
  detail: string;
  /** Tooltip with the exact count. */
  title: string;
}

const SOURCE: Readonly<Record<FoodSource, { label: string; tone: TrustTone; meaning: string }>> = {
  usda_foundation: { label: 'Lab-tested (USDA)', tone: 'lab', meaning: 'Measured in a lab by the US Department of Agriculture.' },
  usda_sr_legacy: { label: 'Lab-tested (USDA)', tone: 'lab', meaning: 'Measured in a lab by the US Department of Agriculture.' },
  usda_survey: { label: 'USDA typical recipe', tone: 'lab', meaning: 'USDA’s values for how this dish is usually made (national food survey).' },
  usda_branded: { label: 'Package label', tone: 'label', meaning: 'Copied from the product’s Nutrition Facts label (USDA branded foods).' },
  community: { label: 'Package label, checked', tone: 'label', meaning: 'Read from a package photo by a Mighty Peanut user and checked before it was shared.' },
  barcode: { label: 'Package label', tone: 'label', meaning: 'From the product’s label: a barcode lookup or your photo of it.' },
  user: { label: 'Your entry', tone: 'mine', meaning: 'Numbers you typed in.' },
  ai: { label: 'AI estimate', tone: 'ai', meaning: 'Estimated by AI from the name and details. Double-check it.' },
  recipe: { label: 'Your recipe', tone: 'recipe', meaning: 'Worked out from the ingredients and cooking you entered.' },
};

export function detailFor(count: number): string {
  if (count >= 60) return 'Full nutrient detail';
  if (count >= 25) return 'Good nutrient detail';
  if (count >= 8) return 'Label nutrients only';
  return 'Calories and macros only';
}

/** @param aiCount how many of the values were filled by AI (shown in the detail line). */
export function foodTrust(source: FoodSource, count: number, aiCount: number = 0): Trust {
  const s = SOURCE[source] ?? { label: 'Food', tone: 'label' as const, meaning: '' };
  const detail: string = aiCount > 0 ? `${detailFor(count)}, ${aiCount} filled by AI` : detailFor(count);
  return {
    label: s.label,
    tone: s.tone,
    meaning: s.meaning,
    detail,
    title: `${s.meaning} ${count} of ${TOTAL_NUTRIENTS} nutrients listed; missing ones show as “—”.`.trim(),
  };
}
